import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
// @ts-ignore
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import 'antd/dist/antd.css';
import { Provider } from 'models';
import { TextareaAutosize, TextField } from '@material-ui/core';
import moment from 'moment';
import { IHardBooking } from './InstantAvailability';
import { Colors, Text } from 'library';

export default function TimeAndRate({
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    hourlyRate,
    setHourlyRate,
    bookedJob,
    minRate,
    provider,
    dateValue,
    headline,
    setHeadline,
    details,
    setDetails,
}: {
    startTime?: Date;
    endTime?: Date;
    setStartTime: (d: Date) => void;
    setEndTime: (d: Date) => void;
    setHourlyRate: (n: number) => void;
    hourlyRate: number;
    bookedJob?: IHardBooking;
    minRate: number;
    provider?: Provider;
    dateValue: Date;
    details: string;
    setDetails: (s: string) => void;
    setHeadline: (s: string) => void;
    headline: string;
}) {
    function endError() {
        return (
            !!endTime &&
            ((bookedJob && moment(bookedJob?.end_date).isBefore(moment(endTime))) ||
                moment(endTime).isBefore(moment(startTime)))
        );
    }
    function startError() {
        return (
            !!startTime &&
            ((bookedJob && moment(bookedJob?.start_date).isAfter(moment(startTime))) ||
                moment(startTime).isAfter(moment(endTime)))
        );
    }

    function endErrorText() {
        if (endError()) {
            return bookedJob && moment(bookedJob?.end_date).isBefore(moment(endTime))
                ? `End time is after indicated availability ${moment(bookedJob?.end_date).format(
                      'hh:mm a',
                  )}, this is ok but may cause the job to be declined due to time conflict.`
                : `End time must be after start time.`;
        }
        return '';
    }

    function startErrorText() {
        if (startError()) {
            return bookedJob && moment(bookedJob?.start_date).isAfter(moment(startTime))
                ? `Start time is before indicated availability ${moment(bookedJob?.start_date).format(
                      'hh:mm a',
                  )}, this is ok but may cause the job to be declined due to time conflict.`
                : `Start time must be before end time.`;
        }
        return '';
    }

    function handleDateChange(s: string, dateSetter: (nd: Date) => void) {
        let nd = new Date(dateValue!.getTime());
        let params = s.split(' ')[0].split(':');
        nd.setHours(parseInt(params[0]), parseInt(params[1]));
        dateSetter(nd);
    }

    return (
        <Grid container item direction="column" xs={12} style={{ justifyContent: 'center', padding: 20 }}>
            <TextField
                style={{ marginBottom: 10 }}
                label="Headline*"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                error={!headline.trim()}
                helperText={'Help your job get noticed'}
            />
            <Grid style={{ marginBottom: 10 }}>
                <Text variant="caption" textStyle={{ color: details.trim().length === 0 ? Colors.red : Colors.grey }}>
                    Job Details*
                </Text>
                <TextareaAutosize
                    rowsMin={3}
                    style={{ width: '100%' }}
                    defaultValue={details}
                    onChange={(e) => setDetails(e.target.value)}
                />
                <Text variant="caption" textStyle={{ color: details.trim().length === 0 ? Colors.red : Colors.grey }}>
                    Give your provider insight on what the job entails.
                </Text>
            </Grid>
            <TextField
                style={{ marginBottom: 10 }}
                id="Start Time"
                label="Start Time"
                type="time"
                defaultValue={moment(bookedJob?.start_date ?? startTime).format('HH:mm')}
                onChange={(e) => handleDateChange(e.target.value, setStartTime)}
                error={startError()}
                helperText={startErrorText()}
                InputLabelProps={{
                    shrink: true,
                }}
                inputProps={{
                    step: 300, // 5 min
                }}
            />
            <TextField
                style={{ marginBottom: 10 }}
                id="End Time"
                label="End Time"
                type="time"
                defaultValue={moment(bookedJob?.end_date ?? endTime).format('HH:mm')}
                onChange={(e) => handleDateChange(e.target.value, setEndTime)}
                InputLabelProps={{
                    shrink: true,
                }}
                error={endError()}
                helperText={endErrorText()}
                inputProps={{
                    step: 300, // 5 min
                }}
            />

            <CurrencyTextField
                label={'Hourly Rate'}
                variant="standard"
                error={provider && hourlyRate && hourlyRate < minRate}
                helperText={
                    provider && hourlyRate && hourlyRate < minRate
                        ? `Hourly Rate must be at provider's minimum rate $${minRate}`
                        : ''
                }
                value={hourlyRate}
                currencySymbol="$"
                outputFormat="string"
                onChange={(event: any, value: number) => setHourlyRate(value)}
            />
        </Grid>
    );
}
